import { useMemo } from 'react';
import { isOrgOwner } from '@/utils/moduleAccess';
import { useAppSelector } from './useAppSelector';

export function usePermissions() {
  const user = useAppSelector((s) => s.auth.user);

  return useMemo(() => {
    const permissions = user?.permissions ?? [];
    const roleCode = user?.roleCode;
    const isMaster = user?.masterAdmin === true;
    const owner = isOrgOwner(permissions, roleCode);
    const has = (code: string) =>
      isMaster || owner || permissions.includes('MASTER_ADMIN') || permissions.includes(code);
    const hasAny = (...codes: string[]) => codes.some(has);
    const hasAll = (...codes: string[]) => codes.every(has);
    const isCashierOnly =
      !isMaster &&
      has('BILLING_CREATE') &&
      !has('ORG_MANAGE') &&
      !has('PRODUCT_MANAGE') &&
      !has('INVENTORY_MANAGE');
    const can = (resource: string, action: string) => {
      const code = `${resource}_${action}`.toUpperCase();
      if (has(code)) return true;
      if (action === 'VIEW' && has(`${resource}_MANAGE`)) return true;
      if (['EDIT', 'CREATE', 'DELETE', 'APPROVE', 'REJECT', 'EXPORT', 'IMPORT'].includes(action.toUpperCase())
          && has(`${resource}_MANAGE`)) {
        return true;
      }
      if (action.toUpperCase() === 'APPROVE' && has('APPROVAL_ACT')) return true;
      return false;
    };
    return { permissions, roleCode, isMaster, has, hasAny, hasAll, isCashierOnly, can };
  }, [user]);
}
